<template lang="pug">
//- SIMPLE ROW MODE (used for non-session items: meals, breaks, etc.)
.session-card.session-card--simple(v-if="mode === 'simple'")
  .session-card__time-block
    .session-card__date-label(v-if="showDate && dateDisplay") {{ dateDisplay }}
    .session-card__time {{ timeDisplay }}
  .session-card__simple-body
    .session-card__icon-wrap
      SessionTypeIcon(:session-type="sessionType")
    .session-card__info
      .session-card__title.session-card__title--simple {{ result.title }}
      .session-card__location(v-if="location")
        svg.session-card__loc-icon(viewBox="0 0 16 16" width="16" height="16" fill="none")
          path(d="M8 1C5.24 1 3 3.24 3 6c0 3.75 5 9 5 9s5-5.25 5-9c0-2.76-2.24-5-5-5z" stroke="currentColor" stroke-width="1.5")
          circle(cx="8" cy="6" r="2" stroke="currentColor" stroke-width="1.5")
        span {{ location }}

//- EXPANDED CARD MODE
.session-card.session-card--expanded(v-else-if="mode === 'expanded'" :class="{ 'session-card--meal': isNonSessionItem }" @click="navigateToDetail")
  .session-card__time-block
    .session-card__date-label(v-if="showDate && dateDisplay") {{ dateDisplay }}
    .session-card__time {{ timeDisplay }}
  .session-card__main
    //- Location
    .session-card__location(v-if="location")
      svg.session-card__loc-icon(viewBox="0 0 16 16" width="16" height="16" fill="none")
        path(d="M8 1C5.24 1 3 3.24 3 6c0 3.75 5 9 5 9s5-5.25 5-9c0-2.76-2.24-5-5-5z" stroke="currentColor" stroke-width="1.5")
        circle(cx="8" cy="6" r="2" stroke="currentColor" stroke-width="1.5")
      span {{ location }}

    //- Tags row (format + topics) — hidden for meals/break or when no tags
    .session-card__tags-row(v-if="!isNonSessionItem && (sessionType || sessionTopics.length)")
      span.session-card__tag.session-card__tag--type(v-if="sessionType") {{ sessionType }}
      span.session-card__tag.session-card__tag--topic(v-for="topic in sessionTopics" :key="topic") {{ topic }}

    //- Top-right badges (prereg + credit)
    .session-card__top-badges
      span.session-card__tag.session-card__tag--prereg(v-if="preregRequired") Pre-registration Required
      span.session-card__tag.session-card__tag--credit(v-if="creditType") {{ creditType }}

    //- Title
    h3.session-card__title.session-card__title--expanded
      button.session-card__title-link(
        v-if="externalId"
        type="button"
        @click.stop="navigateToDetail") {{ result.title }}
      span(v-else) {{ result.title }}

    //- Description
    div.session-card__desc(v-if="result.summary" v-html="sanitizedSummary")

    //- Stacked speaker avatars — hidden for meals/break
    .session-card__speakers(v-if="!isNonSessionItem")
      template(v-if="!speakers.length")
        .session-card__avatar.session-card__avatar--tba
          svg(viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.5")
            path(d="M12 12c2.485 0 4.5-2.015 4.5-4.5S14.485 3 12 3 7.5 5.015 7.5 7.5 9.515 12 12 12z")
            path(d="M3 21c0-4.97 4.03-9 9-9s9 4.03 9 9")
        .session-card__speaker-info
          span.session-card__speaker-label Speaker TBA
      template(v-else)
        .session-card__avatar-group
          .session-card__avatar(
            v-for="(speaker, i) in visibleSpeakers"
            :key="speaker.id || i"
            :style="{ marginLeft: i > 0 ? '-10px' : '0', zIndex: visibleSpeakers.length - i, background: speaker.photoUrl ? undefined : avatarColor(i) }")
            img.session-card__avatar-img(
              v-if="speaker.photoUrl"
              :src="speaker.photoUrl"
              :alt="speaker.name")
            span(v-else) {{ initials(speaker.name) }}
          .session-card__avatar.session-card__avatar--overflow(
            v-if="overflowCount > 0"
            :style="{ marginLeft: '-10px', zIndex: 0 }")
            span +{{ overflowCount }}
        .session-card__speaker-info
          span.session-card__speaker-label {{ speakerDisplay }}
          span.session-card__speaker-subtitle(v-if="speakerSubtitle") {{ speakerSubtitle }}

    //- Sponsor
    .session-card__sponsor-badge(v-if="sponsorName")
      span.session-card__tag.session-card__tag--sponsor &#9733; {{ sponsorName }}

    //- Action buttons — hidden for meals/break
    .session-card__actions(v-if="!isNonSessionItem")
      button.session-card__btn-add(type="button" @click.stop="onAddClick($event)")
        span Add
        svg(xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 17 17" fill="none")
          path(d="M8.5 16.5C12.9183 16.5 16.5 12.9183 16.5 8.5C16.5 4.08172 12.9183 0.5 8.5 0.5C4.08172 0.5 0.5 4.08172 0.5 8.5C0.5 12.9183 4.08172 16.5 8.5 16.5Z" stroke="#163874" style="stroke:#163874;stroke:color(display-p3 0.0863 0.2196 0.4549);stroke-opacity:1;" stroke-linecap="round" stroke-linejoin="round")
          path(d="M5.07153 8.5H11.9287" stroke="#163874" style="stroke:#163874;stroke:color(display-p3 0.0863 0.2196 0.4549);stroke-opacity:1;" stroke-linecap="round" stroke-linejoin="round")
          path(d="M8.5 5.07153V11.9287" stroke="#163874" style="stroke:#163874;stroke:color(display-p3 0.0863 0.2196 0.4549);stroke-opacity:1;" stroke-linecap="round" stroke-linejoin="round")
      button.session-card__btn-view(v-if="externalId" type="button" @click.stop="navigateToDetail")
        span View Details
        svg(width="16" height="8" viewBox="0 0 16 8" fill="none" xmlns="http://www.w3.org/2000/svg")
          path(d="M12.2277 7.56097C12.0977 7.56097 11.9676 7.49593 11.9025 7.43089C11.7074 7.23577 11.7074 6.91057 11.9025 6.71545L14.7643 3.78862L11.8375 0.861789C11.6424 0.666667 11.6424 0.341463 11.8375 0.146341C12.0326 -0.0487805 12.3578 -0.0487805 12.553 0.146341L15.805 3.39837C16.0001 3.5935 16.0001 3.9187 15.805 4.11382L12.553 7.36585C12.4879 7.49593 12.3578 7.56097 12.2277 7.56097Z" fill="#666664" style="fill:#666664;fill:color(display-p3 0.4000 0.4000 0.3922);fill-opacity:1;")
          path(d="M15.4797 4.30896H0.520325C0.260163 4.30896 0 4.11384 0 3.78864C0 3.46343 0.260163 3.26831 0.520325 3.26831H15.4797C15.7398 3.26831 16 3.46343 16 3.78864C16 4.11384 15.7398 4.30896 15.4797 4.30896Z" fill="#666664" style="fill:#666664;fill:color(display-p3 0.4000 0.4000 0.3922);fill-opacity:1;")


//- COMPACT CARD MODE
.session-card.session-card--compact(v-else @click="navigateToDetail" :class="{ 'session-card--meal': isNonSessionItem }")
  .session-card__compact-left
    .session-card__date-label(v-if="showDate && dateDisplay") {{ dateDisplay }}
    //- Meta row: time + dot + location + dot + speaker
    .session-card__compact-meta
      span.session-card__compact-time(v-if="timeDisplay") {{ timeDisplay }}
      .session-card__dot(v-if="timeDisplay && (location || !isNonSessionItem)")
      .session-card__location(v-if="location")
        svg.session-card__loc-icon(viewBox="0 0 16 16" width="16" height="16" fill="none")
          path(d="M8 1C5.24 1 3 3.24 3 6c0 3.75 5 9 5 9s5-5.25 5-9c0-2.76-2.24-5-5-5z" stroke="currentColor" stroke-width="1.5")
          circle(cx="8" cy="6" r="2" stroke="currentColor" stroke-width="1.5")
        span {{ location }}
      .session-card__dot(v-if="location && !isNonSessionItem")
      span.session-card__compact-speaker(v-if="!isNonSessionItem") {{ speakerDisplay }}

    //- Title
    button.session-card__title-link.session-card__title--compact(
      v-if="externalId"
      type="button"
      @click.stop="navigateToDetail") {{ result.title }}
    span.session-card__title--compact(v-else) {{ result.title }}

    //- Tags — strict order: Format, Pre-registration, Credit, Topics
    .session-card__compact-tags
      span.session-card__tag.session-card__tag--type(v-if="sessionType") {{ sessionType }}
      span.session-card__tag.session-card__tag--prereg(v-if="preregRequired") Pre-registration Required
      span.session-card__tag.session-card__tag--credit(v-if="creditType") {{ creditType }}
      span.session-card__tag.session-card__tag--topic(v-for="topic in sessionTopics" :key="topic") {{ topic }}

    //- Sponsor
    .session-card__sponsor-badge(v-if="sponsorName")
      span.session-card__tag.session-card__tag--sponsor &#9733; {{ sponsorName }}

  .session-card__compact-actions
    button.session-card__compact-btn-add(type="button" @click.stop="onAddClick($event)" aria-label="Add to schedule")
      img(src="/img/icons/schedule/md/add.svg" alt="" style="width:16px;height:16px;" aria-hidden="true")
    button.session-card__compact-btn-view(v-if="externalId" type="button" @click.stop="navigateToDetail" aria-label="View details")
      svg(width="10" height="18" viewBox="0 0 10 18" fill="none" xmlns="http://www.w3.org/2000/svg")
        path(d="M1 17L9 9L1 1" stroke="#666664" style="stroke:#666664;stroke:color(display-p3 0.4000 0.4000 0.3922);stroke-opacity:1;" stroke-width="2" stroke-linecap="round" stroke-linejoin="round")
</template>

<script>
import SessionTypeIcon from './SessionTypeIcon.vue';
import { formatTime } from './scheduleTimeUtils.js';
import { formatDayLabel, isNonSessionItem as checkNonSessionItem } from './scheduleResultGrouping.js';
import { summaryHtml } from './sanitizeHtml.js';

export default {
  name: 'ScheduleSessionCard',

  components: {
    SessionTypeIcon,
  },

  props: {
    result: {
      type: Object,
      required: true,
    },
    /** 'expanded' | 'compact' | 'simple' — set by parent based on card mode logic */
    mode: {
      type: String,
      default: 'expanded',
      validator: v => ['expanded', 'compact', 'simple'].includes(v),
    },
    showDate: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    sanitizedSummary() {
      return summaryHtml(this.result.summary);
    },

    sessionType() {
      return (this.result.meta && this.result.meta.sessionType) || '';
    },

    isNonSessionItem() {
      return checkNonSessionItem(this.sessionType);
    },

    creditType() {
      const value = (this.result.meta && this.result.meta.sessionCreditType) || '';
      if (!value || value === 'Not for Credit') return '';
      return value;
    },

    sponsorName() {
      return (this.result.meta && this.result.meta.sponsorName) || '';
    },

    preregRequired() {
      const value = this.result.meta && this.result.meta.preregRequired;
      if (value === undefined || value === null || value === false) return false;
      // Treat anything else truthy as required, except the string 'false' / number 0.
      if (value === 'false' || value === 0 || value === '0') return false;
      return Boolean(value);
    },

    sessionTopics() {
      return (this.result.meta && Array.isArray(this.result.meta.sessionTopics)) ? this.result.meta.sessionTopics : [];
    },

    speakers() {
      if (!this.result.meta || !this.result.meta.speakers) return [];
      return Array.isArray(this.result.meta.speakers) ? this.result.meta.speakers : [];
    },

    speakerDisplay() {
      const count = this.speakers.length;
      if (count === 0) return 'Speaker TBA';
      if (count === 1) return this.speakers[0].name;
      if (count === 2) return `${this.speakers[0].name}, ${this.speakers[1].name}`;
      if (count === 3) return `${this.speakers[0].name}, ${this.speakers[1].name} +1`;
      return 'Multiple Speakers';
    },

    speakerSubtitle() {
      const count = this.speakers.length;
      if (count === 0) return '';
      if (count === 1) return this.formatSpeakerDetail(this.speakers[0]);
      if (count === 2) {
        const bothHaveData = this.speakers.every(s => s.jobTitle || s.organization);
        if (bothHaveData) {
          return `${this.formatSpeakerDetail(this.speakers[0])} • ${this.formatSpeakerDetail(this.speakers[1])}`;
        }
        return 'Multiple Roles, Organizations';
      }
      if (count === 3) return 'Multiple Roles, Organizations';
      // 4+ speakers: show shared org if all speakers have identical non-empty org
      const orgs = this.speakers.map(s => s.organization);
      const uniqueOrgs = [...new Set(orgs.filter(Boolean))];
      if (uniqueOrgs.length === 1 && orgs.every(o => o)) return uniqueOrgs[0];
      return 'Multiple Roles, Organizations';
    },

    visibleSpeakers() {
      return this.speakers.slice(0, 3);
    },

    overflowCount() {
      return Math.max(0, this.speakers.length - 3);
    },

    location() {
      return (this.result.meta && this.result.meta.sessionRoom) || '';
    },

    externalId() {
      return (this.result.meta && this.result.meta.externalId) || null;
    },

    dynamicSubpageSlug() {
      return (this.result.meta && this.result.meta.dynamicSubpageSlug) || null;
    },

    timeDisplay() {
      const start = this.formatTime(this.result.meta && this.result.meta.startDate);
      const end = this.formatTime(this.result.meta && this.result.meta.endDate);
      if (start && end) return `${start}-${end}`;
      return start || '';
    },

    dateDisplay() {
      const startDate = this.result.meta && this.result.meta.startDate;
      if (!startDate) return '';
      return formatDayLabel(startDate.slice(0, 10));
    },

  },

  methods: {
    onAddClick(e) {
      this.$emit('add-session', { result: this.result, triggerEl: e.currentTarget });
    },

    navigateToDetail() {
      if (!this.externalId) return;
      const detailUrl = `${this.$root.parentPageUrl}/sessions/${this.dynamicSubpageSlug}`;
      history.pushState({ swapcardSessionId: this.externalId, fromListing: true }, '', detailUrl);
      this.$emit('navigate-detail', { swapcardSessionId: this.externalId });
    },

    formatTime,

    initials(name) {
      const parts = (name || '').split(' ').filter(Boolean);
      if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      return parts[0] ? parts[0][0].toUpperCase() : '';
    },

    avatarColor(index) {
      const colors = ['#2563EB', '#0F766E', '#10B981', '#036EBA'];
      return colors[index % colors.length];
    },

    formatSpeakerDetail(speaker) {
      return [speaker.jobTitle, speaker.organization].filter(Boolean).join(', ');
    },
  },
};
</script>

<style lang="scss" scoped>
@import './tokens';

// ---------- Shared ----------
.session-card__time {
  color: $color-primary;
  font-size: 17px;
  font-weight: 700;
  line-height: 17px;
  white-space: nowrap;
  min-width: 170px;
}

.session-card__date-label {
  color: $color-text-secondary;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  margin-bottom: 2px;
  white-space: nowrap;
}

.session-card__location {
  display: flex;
  align-items: center;
  gap: 4px;
  color: $color-text-secondary;
  font-size: 14px;
  font-weight: 500;
}

.session-card__loc-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: $color-text-secondary;
}

// ---------- Tag pills ----------
.session-card__tag {
  height: 24px;
  padding: 4px 12px;
  border-radius: 20000px;
  font-size: 13px;
  font-weight: 500;
  line-height: 19.5px;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;

  &--type {
    background: $color-tag-type-bg;
    color: $color-tag-type-fg;
  }

  &--topic {
    background: $color-tag-topic-bg;
    color: $color-tag-topic-fg;
  }

  &--credit {
    background: $color-tag-credit-bg;
    color: $color-tag-credit-fg;
  }

  &--prereg {
    background: $color-tag-prereg-bg;
    color: $color-tag-prereg-fg;
  }

  &--sponsor {
    background: $color-tag-sponsor-bg;
    color: $color-tag-sponsor-fg;
  }
}

// ---------- Title link (shared between expanded and compact) ----------
.session-card__title-link {
  color: $color-text-dark;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid $color-primary;
    outline-offset: 2px;
    border-radius: 2px;
  }
}

// ---------- SIMPLE ROW ----------
.session-card--simple {
  width: 100%;
  padding: 8px 16px;
  background: white;
  border-radius: 12px;
  border: 1px solid $color-border;
  position: relative;
  min-height: 56px;
  margin-bottom: 24px;

  .session-card__time-block {
    position: absolute;
    top: 50%;
    left: 16px;
    transform: translateY(-50%);
  }

  .session-card__simple-body {
    margin-left: 194px;
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 8px 0;
    min-width: 0;
  }

  .session-card__icon-wrap {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    background: $color-surface-sunken;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .session-card__info {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
  }

  .session-card__title--simple {
    color: $color-text-dark;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.35;
    letter-spacing: -0.18px;
    margin: 0;
  }
}

// ---------- EXPANDED CARD ----------
.session-card--expanded {
  width: 100%;
  padding: 24px 32px 32px 16px;
  background: white;
  border-top: 1.5px solid $color-border;
  border-right: 1.5px solid $color-border;
  border-bottom: 1.5px solid $color-border;
  border-left: 3px solid $color-cta-bg;
  border-radius: 12px;
  box-shadow: $shadow-card;
  position: relative;
  min-height: 180px;
  cursor: pointer;

  &.session-card--meal {
    border-left-color: $color-border-card-meal;
    cursor: default;
    &:hover {
      background: white;
    }
  }

  &:hover {
    background: $color-surface-hover;
  }

  .session-card__time-block {
    position: absolute;
    top: 24px;
    left: 16px;
  }

  .session-card__main {
    margin-left: 194px;
  }

  .session-card__tags-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 16px;
  }

  .session-card__top-badges {
    position: absolute;
    top: 24px;
    right: 33px;
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    max-width: 320px;
    justify-content: flex-end;
  }

  .session-card__title--expanded {
    color: $color-text-dark;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.35;
    margin: 24px 0 0;
  }

  .session-card__desc {
    color: $color-text-desc;
    font-size: 15px;
    font-weight: 400;
    line-height: 1.6;
    margin: 0;
    max-width: 680px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;

    ::v-deep .tm-symbol {
      font-size: 0.6em;
      vertical-align: 0;
      line-height: 0;
    }

    ::v-deep p {
      margin: 0 0 16px;
    }
  }

  .session-card__speakers {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 24px;
    margin-bottom: 12px;
  }

  .session-card__avatar-group {
    display: flex;
    align-items: center;
  }

  .session-card__avatar {
    width: 44px;
    height: 44px;
    border-radius: 9999px;
    background: $color-primary-dark; // fallback; overridden by inline style for initials
    color: white;
    font-size: 13px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid white;
    flex-shrink: 0;
    position: relative;
    overflow: hidden;

    &--overflow {
      background: $color-bg-secondary;
      color: $color-text-secondary;
    }

    &--tba {
      background: $color-bg-secondary;
      color: $color-text-secondary;
    }

    .session-card__avatar-img {
      width: 100%;
      height: 100%;
      border-radius: 9999px;
      object-fit: cover;
      display: block;
    }
  }

  .session-card__speaker-label {
    color: $color-text-dark;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.4;
  }

  .session-card__speaker-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .session-card__speaker-subtitle {
    color: $color-text-secondary;
    font-size: 13px;
    font-weight: 400;
    line-height: 1.4;
  }

  .session-card__sponsor-badge {
    margin-bottom: 12px;
  }

  .session-card__actions {
    display: flex;
    gap: 16px;
    justify-content: flex-end;
    margin-top: 24px;
  }

  .session-card__btn-add {
    height: 32px;
    padding: 0 14px;
    background: white;
    border-radius: 6px;
    border: 1px solid $color-primary;
    color: $color-primary;
    font-size: 14px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    box-shadow: $shadow-btn;

    &:hover {
      background: $color-btn-outline-hover-bg;
      border-color: $color-btn-outline-hover-border;
    }

    &:focus-visible {
      outline: 2px solid $color-primary;
      outline-offset: 2px;
    }
  }

  .session-card__btn-add svg {
    height: 16px;
    width: auto;
  }

  .session-card__btn-view svg {
    height: auto;
    width: 16px;
  }

  .session-card__btn-view {
    height: 32px;
    padding: 0 14px;
    background: $color-cta-accent;
    border-radius: 6px;
    border: none;
    color: $color-primary;
    font-size: 13px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    box-shadow: $shadow-btn-subtle;

    &:hover {
      background: $color-cta-accent-hover;
    }

    &:focus-visible {
      outline: 2px solid $color-primary;
      outline-offset: 2px;
    }
  }
}

// ---------- COMPACT CARD ----------
.session-card--compact {
  width: 100%;
  padding: 14px 16px;
  background: white;
  border: 1px solid $color-border;
  border-radius: 0;
  box-shadow: $shadow-card;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  cursor: pointer;

  &.session-card--meal {
    cursor: default;
    &:hover {
      background: white;
    }
  }

  &:hover {
    background: $color-surface-hover;
  }

  .session-card__compact-left {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
  }

  .session-card__compact-meta {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .session-card__compact-time {
    color: $color-primary;
    font-size: 14px;
    font-weight: 700;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .session-card__dot {
    width: 8px;
    height: 8px;
    background: $color-text-secondary;
    border-radius: 9999px;
    flex-shrink: 0;
  }

  .session-card__compact-speaker {
    color: $color-text-secondary;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .session-card__title--compact {
    color: $color-text-compact;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .session-card__compact-tags {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .session-card__compact-actions {
    display: flex;
    gap: 16px;
    flex-shrink: 0;
  }

  .session-card__compact-btn-add {
    width: 40px;
    height: 40px;
    padding: 12px;
    background: white;
    border-radius: 8px;
    border: 1px solid $color-border;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: $color-primary;

    &:hover {
      background: $color-surface-hover;
      border-color: $color-btn-outline-hover-border;
    }

    &:focus-visible {
      outline: 2px solid $color-primary;
      outline-offset: 2px;
    }
  }
  .session-card__compact-btn-add svg {
    width: 100%;
    height: 100%;
  }

  .session-card__compact-btn-view {
    width: 40px;
    height: 40px;
    padding: 12px;
    background: $color-cta-accent;
    border-radius: 8px;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: $shadow-btn-subtle;
    color: $color-primary;

    &:hover {
      background: $color-cta-accent-hover;
    }

    &:focus-visible {
      outline: 2px solid $color-primary;
      outline-offset: 2px;
    }
  }

  .session-card__compact-btn-view svg {
    height: 100%;
    width: auto;
  }
}

@media (max-width: 1023px) {
  .session-card--expanded {
    min-height: 0;

    .session-card__time-block {
      position: static;
    }

    .session-card__main {
      margin-left: 0;
      margin-top: 8px;
    }

    .session-card__title--expanded {
      margin-top: 8px;
    }

    .session-card__top-badges {
      position: static;
      max-width: none;
      justify-content: flex-start;
      margin-bottom: 8px;
    }
  }

  .session-card--compact {
    flex-direction: column;
    align-items: stretch;

    .session-card__compact-actions {
      justify-content: flex-end;
    }
  }

  .session-card--simple {
    min-height: 0;
    padding: 20px 16px;

    .session-card__time-block {
      position: static;
      transform: none;
    }

    .session-card__simple-body {
      margin-left: 0;
      margin-top: 8px;
    }
  }
}

@media (max-width: 991px) {
  .session-card--compact {
    .session-card__compact-meta {
      flex-wrap: wrap;
    }

    .session-card__compact-time {
      flex-basis: 100%;
    }

    // Hide the dot between time and location/speaker — it ends up orphaned on the second line
    .session-card__compact-time + .session-card__dot {
      display: none;
    }

    // Hide the dot between location and speaker, and put speaker on its own line
    .session-card__location + .session-card__dot {
      display: none;
    }

    .session-card__compact-speaker {
      flex-basis: 100%;
    }
  }
}
</style>
